import React, { Dispatch, useState } from 'react'
import { Tab, Tabs, TextField } from '@mui/material'
import { CREATIVE_IMPORT_LIMITS } from '@marketing-milk/interfaces'
import { CreativeImportAction, EditableSlideField, RowMediaPreview, SlideUIState } from '../state'

interface Props {
  rowNumber: number
  slides: SlideUIState[]
  previews: RowMediaPreview[]
  disabled: boolean
  dispatch: Dispatch<CreativeImportAction>
}

/**
 * Per-slide copy for a carousel row, one tab per card.
 *
 * Tabs rather than a stacked list: a carousel is 2–5 cards each with three
 * fields, and stacking them all pushes the creative-level copy off screen —
 * whereas the slide you're editing is the only one whose fields you need.
 *
 * Patterned on the creative wizard's `CarouselSlides`, deliberately NOT importing
 * it: that one is MUI v4 + react-hook-form + the media-library picker, and this
 * surface is MUI v5 + reducer with media fixed by the manifest.
 */
export function SlideEditor({ rowNumber, slides, previews, disabled, dispatch }: Props) {
  const [active, setActive] = useState(0)
  // A row's slide count is fixed by the manifest, but the active tab is local
  // state that outlives a row change — clamp rather than render an empty panel.
  const index = Math.min(active, slides.length - 1)
  const slide = slides[index]
  if (!slide) return null

  const edit = (field: EditableSlideField, value: string) =>
    dispatch({ type: 'editSlideField', rowNumber, ordinal: slide.ordinal, field, value })

  const still = (preview?: RowMediaPreview) =>
    preview?.kind === 'video' ? preview.posterUrl : preview?.sourceUrl

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        value={index}
        onChange={(_, next) => setActive(next)}
        variant="scrollable"
        scrollButtons="auto"
        className="min-h-0 border-b"
      >
        {slides.map((_, ordinal) => {
          const thumb = still(previews.find(preview => preview.ordinal === ordinal))
          return (
            <Tab
              key={ordinal}
              className="min-h-0"
              // The thumbnail IS the label: "Card 3" doesn't tell you which card
              // you're on when the whole point is matching copy to an image.
              icon={
                thumb ? (
                  <img src={thumb} alt="" className="h-8 w-8 rounded object-cover" />
                ) : (
                  <div className="h-8 w-8 rounded bg-gray-200" />
                )
              }
              iconPosition="start"
              label={`Card ${ordinal + 1}`}
            />
          )
        })}
      </Tabs>

      <TextField
        size="small"
        fullWidth
        label={`Card ${index + 1} headline`}
        placeholder="Added in Builder"
        value={slide.headline}
        disabled={disabled}
        // Capped here as well as at Confirm: the carousel-slide entity is
        // varchar(255) on headline and description, and a value that only fails in the
        // worker fails past the point the AE can fix it.
        inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.headlineMaxLength }}
        onChange={e => edit('headline', e.target.value)}
      />
      <TextField
        size="small"
        fullWidth
        label={`Card ${index + 1} description`}
        placeholder="Added in Builder"
        value={slide.description}
        disabled={disabled}
        inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.headlineMaxLength }}
        onChange={e => edit('description', e.target.value)}
      />
      <TextField
        size="small"
        fullWidth
        label={`Card ${index + 1} link`}
        placeholder="Uses the ad's destination URL"
        value={slide.destinationUrl}
        disabled={disabled}
        inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength }}
        onChange={e => edit('destinationUrl', e.target.value)}
      />
    </div>
  )
}
